import { useMemo } from "react";
import { format, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight, EyeOff, Plus, Repeat } from "lucide-react";
import { bucketEventsByLocalDay, monthAgendaWindow } from "../../../lib/days";
import { getBrowserTimeZone } from "../../../lib/time";
import { dayHasConflict } from "../utils/overlap";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";

// Tap-friendly list replacement for the react-big-calendar grid on phones.
// The desktop month/week/day grid compresses 7 columns into ~40px cells at
// 360-414px, making marks/events unreadable and untappable. This renders the
// same `events` feed as a chronological, day-grouped agenda: marks are
// tap-to-edit, external calendar events show Busy/Free, and days where a free
// mark/calendar overlaps a busy event are flagged as conflicts.

// Conflict detection + local-day bucketing are shared with the desktop month
// view: `dayHasConflict` is imported from utils/overlap.js and the day buckets
// come from lib/days.bucketEventsByLocalDay, so the two surfaces can NEVER
// disagree about which days conflict or which day an event belongs to.

function eventBadge(event) {
  const r = event.resource || {};
  if (r.kind === "mark") {
    return r.level === "PREFERRED"
      ? { label: "Preferred", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" }
      : { label: "Feasible", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" };
  }
  if (r.kind === "external" && r.calendarType === "free") {
    return { label: "Free", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" };
  }
  if (r.kind === "finalizedSession") {
    return { label: "Session", cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" };
  }
  return { label: "Busy", cls: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300" };
}

function eventTitle(event) {
  const r = event.resource || {};
  const t = typeof event.title === "string" ? event.title.trim() : "";
  if (t) return t;
  if (r.kind === "mark") return r.level === "PREFERRED" ? "Preferred" : "Feasible";
  if (r.kind === "external" && r.calendarType === "free") return "Available";
  if (r.kind === "finalizedSession") return "Session";
  return "Busy";
}

// Shared focus ring so every interactive row meets the visible-focus a11y rule
// (mirrors MonthDateHeader's brand focus token).
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const ROW_BASE =
  "flex min-h-[44px] w-full items-center gap-3 rounded-xl border border-slate-200/70 px-3 py-2 text-left dark:border-slate-700";

// A `row` wraps the ORIGINAL event plus its per-day clip: { event, clippedEnd,
// isContinuation, isAllDay, dayKey }. Callbacks always receive the original
// event (never a per-day clone), so onSelectEvent/onEditMark stay canonical.
function AgendaRow({ row, onEditMark, onSelectEvent }) {
  const { event, clippedEnd, isContinuation, isAllDay, dayKey } = row;
  const r = event.resource || {};
  const badge = eventBadge(event);
  const title = eventTitle(event);
  const endMs = (event.end instanceof Date ? event.end : new Date(event.end)).getTime();

  // Time label by the row's position within a (possibly multi-day) span:
  //   all-day            → "All day"  (+ muted "(continued)" on later days)
  //   timed start day    → "7:00 PM – 1:00 AM"  (range; end < start implies next-day)
  //   timed final day    → "Ends 1:00 AM"  (a tail — never a bare range that
  //                        would read as a separate 1 AM event)
  //   timed full mid-day → "All day"  (rare > 24h timed span)
  let timeLabel;
  let showContinued = false;
  if (isAllDay) {
    timeLabel = "All day";
    showContinued = isContinuation;
  } else if (isContinuation) {
    const endsThisDay = clippedEnd.getTime() >= endMs;
    timeLabel = endsThisDay ? `Ends ${format(event.end, "h:mm a")}` : "All day";
    showContinued = !endsThisDay;
  } else {
    timeLabel = `${format(event.start, "h:mm a")} – ${format(event.end, "h:mm a")}`;
  }

  // The actual resource fields — marks carry isRecurring, external events carry
  // recurringEventId (neither `rrule` nor `recurring` exists on a resource).
  const isRecurring = r.isRecurring || Boolean(r.recurringEventId);
  // The optimistic create placeholder is spliced into `events`; it has no real
  // id and must not render as a tappable row.
  const isMark =
    r.kind === "mark" && r.markId && r.markId !== "__pending-new-mark__";
  const isSession = r.kind === "finalizedSession" && r.schedulerId;
  const isExternal = r.kind === "external";

  const inner = (
    <>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
          {title}
        </p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          {timeLabel}
          {showContinued && (
            <span className="text-slate-500 dark:text-slate-400" aria-hidden="true">
              (continued)
            </span>
          )}
          {isRecurring && (
            <>
              <Repeat className="h-3 w-3" aria-hidden="true" />
              <span className="sr-only">Recurring</span>
            </>
          )}
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}>
        {badge.label}
      </span>
    </>
  );

  // On a continuation row, name the span's true start once for screen readers
  // (the visible "(continued)" is aria-hidden).
  const continuedNote = isContinuation
    ? `, continues from ${format(event.start, "MMM d")}`
    : "";

  if (isMark) {
    return (
      <button
        type="button"
        onClick={() => onEditMark(r.markId)}
        // Continuation rows get a day-suffixed testid so the start-day row's
        // `agenda-mark-<id>` stays unique.
        data-testid={
          isContinuation
            ? `agenda-mark-${r.markId}-cont-${dayKey}`
            : `agenda-mark-${r.markId}`
        }
        aria-label={`Edit availability mark: ${title}${continuedNote}`}
        className={`${ROW_BASE} ${FOCUS_RING} bg-white transition-colors hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700`}
      >
        {inner}
      </button>
    );
  }

  // Parity with the desktop calendar's onSelectEvent: tapping a finalized
  // session opens its scheduler page; tapping an external event opens the
  // hide/manage dialog. Without making these rows interactive a phone user
  // could neither jump to a session nor hide an external event.
  if (isSession || isExternal) {
    return (
      <button
        type="button"
        onClick={() => onSelectEvent(event)}
        aria-label={`${isSession ? `Open session: ${title}` : `Manage event: ${title}`}${continuedNote}`}
        className={`${ROW_BASE} ${FOCUS_RING} bg-white/60 transition-colors hover:bg-slate-50 dark:bg-slate-800/60 dark:hover:bg-slate-700`}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={`${ROW_BASE} bg-white/60 dark:bg-slate-800/60`}>{inner}</div>
  );
}

export function MobileAvailabilityAgenda({
  events = [],
  date = new Date(),
  // Map<localDayKey, hiddenEvent[]> from AvailabilityPage (same shared bucketer
  // as the desktop month-view "# hidden" pip). Populated only when the Show
  // hidden toggle is on; each day with entries gets a tappable pip listing them
  // (audit A-6b) — they are intentionally NOT rendered as agenda rows.
  hiddenByDay = new Map(),
  onNavigate = () => {},
  onEditMark = () => {},
  onSelectEvent = () => {},
  onCreateMark = () => {},
  // Per-day create: the day-header "+" seeds a new mark on THAT day (audit A-6a).
  onCreateMarkForDay = () => {},
}) {
  const groups = useMemo(() => {
    // Scope the list to the DISPLAYED month, but by SPAN OVERLAP rather than
    // start-day: a multi-day or cross-midnight item is fanned under every local
    // day it touches within the month, matching the desktop calendar's spanning
    // (a 5-day vacation that previously showed only on day 1 now appears on
    // every day, and a span starting last month spills correctly into view).
    // Within the CURRENT month, days entirely in the past are dropped so the
    // list opens on what's actionable; other months render in full. Bucketing +
    // conflict detection are the SHARED primitives (lib/days.bucketEventsByLocalDay
    // + utils/overlap.dayHasConflict), so this surface and the desktop month view
    // can never disagree about day attribution or conflicts.
    const browserTz = getBrowserTimeZone();
    const { windowStart, windowEnd } = monthAgendaWindow(date);

    // Drop the optimistic create placeholder spliced into `events` before
    // bucketing.
    const feed = events.filter(
      (e) =>
        e?.start &&
        e?.end &&
        !(
          e.resource?.kind === "mark" &&
          e.resource?.markId === "__pending-new-mark__"
        )
    );
    const byDay = bucketEventsByLocalDay({
      events: feed,
      timeZone: browserTz,
      windowStart,
      windowEnd,
    });
    return [...byDay.values()]
      .map((group) => ({
        key: group.dayKey,
        dayStart: group.dayStart,
        // Conflict detection runs on the ORIGINAL full-range events bucketed
        // under this day, so a multi-day busy event overlapping a free block on
        // a later day correctly trips the pip.
        conflict: dayHasConflict(group.rows.map((row) => row.event)),
        rows: group.rows
          .map((row) => ({
            ...row,
            // Timed multi-day MARKS arrive pre-split into one per-day segment
            // (each clipped to its own local day, sharing one markId) from
            // marks-render's T2 splitter. A non-first segment (segmentIndex > 0)
            // IS a continuation even though its clipped start sits at local
            // midnight — without this, two segments of one mark would both look
            // like start-day rows and collide on the bare `agenda-mark-<id>`
            // testid.
            isContinuation:
              row.isContinuation ||
              (Number.isInteger(row.event.resource?.segmentIndex) &&
                row.event.resource.segmentIndex > 0),
          }))
          .sort((a, b) => a.clippedStart.getTime() - b.clippedStart.getTime()),
      }))
      .sort((a, b) => a.dayStart.getTime() - b.dayStart.getTime());
  }, [events, date]);

  const monthLabel = format(date, "MMMM yyyy");
  const goPrev = () => {
    const d = new Date(date);
    d.setMonth(d.getMonth() - 1);
    onNavigate(d);
  };
  const goNext = () => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + 1);
    onNavigate(d);
  };

  return (
    <div data-testid="availability-mobile-agenda" className="space-y-4">
      <div className="flex items-center justify-center gap-1">
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous month"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 ${FOCUS_RING}`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[8.5rem] text-center text-sm font-semibold tabular-nums">
          {monthLabel}
        </span>
        <button
          type="button"
          onClick={goNext}
          aria-label="Next month"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 ${FOCUS_RING}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          <p>No availability or events in {monthLabel}.</p>
          <button
            type="button"
            onClick={onCreateMark}
            data-testid="agenda-empty-add-mark"
            className={`mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary/90 ${FOCUS_RING}`}
          >
            {/* Accessible name = visible "Add mark" (Label-in-Name); distinct
                from EmptyAvailabilityState's "Add availability mark" so the two
                never collide on the no-calendar mobile screen. */}
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add mark
          </button>
          <p className="mt-3">Tap to share your free time.</p>
        </div>
      ) : (
        groups.map((group) => {
          const dayLabel = format(group.dayStart, "EEE, MMM d");
          const hiddenForDay = hiddenByDay.get(group.key) || [];
          return (
            <div key={group.key}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="min-w-0 truncate text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {isSameDay(group.dayStart, new Date()) ? "Today · " : ""}
                  {dayLabel}
                </p>
                <div className="flex shrink-0 items-center gap-1.5">
                  {group.conflict && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      Conflict
                    </span>
                  )}
                  {hiddenForDay.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`agenda-hidden-pip-${group.key}`}
                          aria-label={`${hiddenForDay.length} hidden event${hiddenForDay.length === 1 ? "" : "s"} on ${dayLabel}`}
                          className={`inline-flex min-h-[36px] items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 ${FOCUS_RING}`}
                        >
                          <EyeOff className="h-3 w-3" aria-hidden="true" />
                          {hiddenForDay.length} hidden
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-3" align="end">
                        <p className="mb-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Hidden events
                        </p>
                        <ul className="space-y-1">
                          {hiddenForDay.map((e, i) => (
                            <li
                              key={`${group.key}-hidden-${i}`}
                              className="flex items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-300"
                            >
                              <span className="min-w-0 truncate">{eventTitle(e)}</span>
                              <span className="shrink-0 text-slate-400 dark:text-slate-500">
                                {e.allDay || e.resource?.allDay
                                  ? "All day"
                                  : format(e.start, "h:mm a")}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </PopoverContent>
                    </Popover>
                  )}
                  <button
                    type="button"
                    onClick={() => onCreateMarkForDay(group.dayStart)}
                    aria-label={`Add mark on ${dayLabel}`}
                    data-testid={`agenda-add-mark-${group.key}`}
                    className={`inline-flex min-h-[44px] items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-brand-primary/40 hover:bg-brand-primary/10 hover:text-brand-primary dark:border-slate-700 dark:text-slate-300 dark:hover:border-brand-primary/50 dark:hover:bg-brand-primary/20 dark:hover:text-blue-300 ${FOCUS_RING}`}
                  >
                    <Plus className="h-3 w-3" aria-hidden="true" /> Add
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {group.rows.map((row, i) => (
                  <AgendaRow
                    key={`${group.key}-${i}`}
                    row={row}
                    onEditMark={onEditMark}
                    onSelectEvent={onSelectEvent}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
